"use client";

import BigNumber from "bignumber.js";
import { useMemo } from "react";
import {
  useWalletPortfolioContext,
  usePortfolioFiat,
} from "../../providers/wallet-portfolio-provider";
import { useForgePools } from "../../queries/use-forge-pools";
import { useForgeTokenDetails } from "../../queries/use-forge-token-details";
import { useCrossUsdPrice } from "../../queries/use-cross-usd-price";
import { APP_LAUNCHER_ICONS_BASE, DEX_WEB } from "../../constants";
import { formatFiatFromUsd, type PortfolioFiat } from "../../utils";
import type { ForgePool, ForgeTokenDetail } from "../../types";
import { SectionCard } from "./SectionCard";
import { ExternalLinkIcon } from "../icons";
import { handleOutlinkClick } from "../../../../lib/outlink";

const SECTION_TITLE = "Forge";

const LP_DECIMALS = 18;

const FALLBACK_ICON =
  "https://contents.crosstoken.io/wallet/token/images/FT.png";

function formatLp(raw: string): string {
  if (!raw || raw === "0") return "0";
  const bn = new BigNumber(raw).shiftedBy(-LP_DECIMALS);
  if (bn.isZero()) return "0";
  return bn.isGreaterThanOrEqualTo(1)
    ? bn.toFormat(2, BigNumber.ROUND_DOWN)
    : bn.toFormat(4, BigNumber.ROUND_DOWN);
}

function formatOwnership(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return "0%";
  if (n >= 1) return `${(Math.floor(n * 100) / 100).toFixed(2)}%`;
  return `${(Math.floor(n * 10000) / 10000).toFixed(4)}%`;
}

/**
 * 사용자 Forge LP 포지션의 USD 환산가. game-swap 풀가치 산식과 동일하게,
 * 풀 총 유동성(CROSS) = 2 × reserve_b(CROSS 쪽 예비량)로 보고:
 *
 *   positionCross = (lp_balance / total_supply) × (2 × reserve_b)
 *   usd           = positionCross × crossUsdPrice
 *
 * reserve_b는 CROSS(네이티브) 쪽이며 18 decimals. detail/예비량/total_supply/
 * 단가 중 하나라도 없거나 0이면 null → 환산가 줄을 숨긴다.
 */
function computeForgeLpUsd(
  pool: ForgePool,
  detail: ForgeTokenDetail | undefined,
  crossUsdPrice: string,
  fiat: PortfolioFiat,
): string | null {
  const price = new BigNumber(crossUsdPrice || "0");
  if (!price.isGreaterThan(0)) return null;

  const totalSupply = new BigNumber(pool.total_supply || "0");
  const lpBalance = new BigNumber(pool.lp_balance || "0");
  const crossReserve = new BigNumber(detail?.reserve_b || "0").shiftedBy(
    -LP_DECIMALS,
  );
  if (
    !totalSupply.isGreaterThan(0) ||
    !lpBalance.isGreaterThan(0) ||
    !crossReserve.isGreaterThan(0)
  ) {
    return null;
  }

  const poolCrossValue = crossReserve.times(2);
  const positionCross = lpBalance.div(totalSupply).times(poolCrossValue);
  const usd = positionCross.times(price);
  if (!usd.isFinite()) return null;
  return formatFiatFromUsd(usd, fiat);
}

export function ForgeSection() {
  const { env, walletAddress, onOutlink } = useWalletPortfolioContext();
  const fiat = usePortfolioFiat();
  const hasAddress = !!walletAddress;
  const { pools, isEmpty, isLoading } = useForgePools(
    env,
    walletAddress,
    hasAddress,
  );

  const tokenAddresses = useMemo(
    () => pools.map((p) => p.token.address),
    [pools],
  );
  const { detailMap } = useForgeTokenDetails(
    env,
    tokenAddresses,
    hasAddress && tokenAddresses.length > 0,
  );

  // CROSS/USD 단가 — LP Balance 아래 $환산가 표기에 사용.
  const crossUsdPrice = useCrossUsdPrice(env, walletAddress, hasAddress);

  const getForgeTokenUrl = (pool: ForgePool) =>
    `${DEX_WEB[env]}/forge/token/${pool.token.address}`;

  const sectionIcon = `${APP_LAUNCHER_ICONS_BASE[env]}/forge.png`;

  return (
    <SectionCard
      sectionKey="forge"
      title={SECTION_TITLE}
      icon={sectionIcon}
      iconFit="contain"
      isEmpty={isEmpty}
      loading={isLoading}
    >
      {pools.map((pool) => {
        const detail = detailMap.get(pool.token.address.toLowerCase());
        const img =
          detail?.image_url || detail?.image || pool.token.image || FALLBACK_ICON;
        const symbol = detail?.symbol || pool.token.symbol;
        const forgeTokenUrl = getForgeTokenUrl(pool);
        const lpUsd = computeForgeLpUsd(pool, detail, crossUsdPrice, fiat);

        return (
          <a
            className="wp-item"
            data-side="lp"
            key={pool.pair_address}
            href={forgeTokenUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-track="forge_outlink"
            data-track-id={symbol}
            onClick={(e) =>
              handleOutlinkClick(
                e,
                forgeTokenUrl,
                {
                  category: "portfolio",
                  origin: "portfolio-forge",
                  payload: { pool, tokenDetail: detail },
                },
                onOutlink,
              )
            }
          >
            <div className="wp-item-body">
              <div className="wp-item-line">
                <span className="wp-item-label">Asset</span>
                <div className="wp-svc-token">
                  <img className="wp-svc-token-icon" src={img} alt="" />
                  <span>{symbol}</span>
                  {detail?.name && (
                    <span className="wp-token-subtle">{detail.name}</span>
                  )}
                </div>
              </div>
              <div className="wp-item-line">
                <span className="wp-item-label">LP Balance</span>
                <div className="wp-svc-token wp-svc-token--amount">
                  <div className="wp-svc-amount">
                    <span className="wp-svc-amount-main">
                      {formatLp(pool.lp_balance)}
                    </span>
                    {lpUsd && <span className="wp-item-usd">{lpUsd}</span>}
                  </div>
                </div>
              </div>
              <div className="wp-item-line">
                <span className="wp-item-label">Ownership</span>
                <span className="wp-line-value">
                  {formatOwnership(pool.pool_ownership)}
                </span>
              </div>
            </div>
            <div className="wp-item-tags">
              <span className="wp-tag wp-tag--muted">
                <span className="wp-tag-label">Withdraw</span>
                <ExternalLinkIcon size={12} className="wp-tag-icon" />
              </span>
            </div>
          </a>
        );
      })}
    </SectionCard>
  );
}
